import { useEffect, useState } from "react";
import { Plus, Target, Check, Trash2, Pencil, Flag } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const PRIORITY = { High: "text-red-300 border-red-400/30", Medium: "text-amber-300 border-amber-400/30", Low: "text-[#71717A] border-white/15" };
const CATS = ["General", "Business Credit", "Funding", "Marketing", "Sales", "Operations", "Compliance", "Growth"];

function fmt(d) { if (!d) return null; try { return new Date(d.length <= 10 ? d + "T00:00:00" : d).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return null; } }

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [dialog, setDialog] = useState(null); // null | {} (new) | goal (edit)
  const [form, setForm] = useState({ title: "", category: "General", priority: "Medium", due_date: "", progress: 0 });

  const load = () => api.get("/progress/goals").then(({ data }) => setGoals(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ title: "", category: "General", priority: "Medium", due_date: "", progress: 0 }); setDialog({}); };
  const openEdit = (g) => { setForm({ title: g.title, category: g.category, priority: g.priority, due_date: g.due_date || "", progress: g.progress || 0 }); setDialog(g); };

  const save = async () => {
    if (!form.title.trim()) return;
    try {
      if (dialog.goal_id) await api.patch(`/progress/goals/${dialog.goal_id}`, { ...form, progress: Number(form.progress) });
      else await api.post("/progress/goals", { ...form, progress: Number(form.progress) });
      toast.success(dialog.goal_id ? "Goal updated" : "Goal added"); setDialog(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const complete = async (g) => { try { await api.patch(`/progress/goals/${g.goal_id}`, { status: "Completed" }); toast.success("Goal completed 🎉"); load(); } catch { toast.error("Failed"); } };
  const del = async (g) => { try { await api.delete(`/progress/goals/${g.goal_id}`); load(); } catch { toast.error("Failed"); } };

  return (
    <div data-testid="goals-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg text-white flex items-center gap-2"><Target className="w-5 h-5 text-[#D4AF37]" /> Custom Goals</h3>
        <Button onClick={openNew} data-testid="add-goal-btn" size="sm" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full"><Plus className="w-4 h-4 mr-1" /> New goal</Button>
      </div>

      {goals.length === 0 ? (
        <div className="text-center py-12 rounded-2xl border border-dashed border-white/10 bg-[#0f0f11]" data-testid="goals-empty">
          <Target className="w-9 h-9 text-[#52525B] mx-auto mb-2" />
          <p className="text-sm text-[#71717A]">No goals yet. Set a goal like "Build business credit" or "Launch website".</p>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="goals-list">
          {goals.map((g) => (
            <div key={g.goal_id} data-testid={`goal-${g.goal_id}`}
              className={`rounded-xl border p-4 ${g.status === "Completed" ? "border-emerald-400/25 bg-emerald-500/5" : "border-white/8 bg-[#121214]"}`}>
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${g.status === "Completed" ? "text-emerald-300 line-through" : "text-white"}`}>{g.title}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border flex items-center gap-0.5 ${PRIORITY[g.priority] || PRIORITY.Medium}`}><Flag className="w-2.5 h-2.5" /> {g.priority}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-[11px] text-[#71717A]">
                    <span>{g.category}</span>{g.due_date && <><span>·</span><span>Due {fmt(g.due_date)}</span></>}
                  </div>
                  {g.status !== "Completed" && <Progress value={g.progress || 0} className="h-1.5 bg-white/10 mt-2" />}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {g.status !== "Completed" && <button onClick={() => complete(g)} data-testid={`goal-complete-${g.goal_id}`} title="Mark complete" className="w-8 h-8 rounded-lg text-[#71717A] hover:text-emerald-400 hover:bg-white/5 flex items-center justify-center"><Check className="w-4 h-4" /></button>}
                  <button onClick={() => openEdit(g)} data-testid={`goal-edit-${g.goal_id}`} title="Edit" className="w-8 h-8 rounded-lg text-[#71717A] hover:text-white hover:bg-white/5 flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(g)} data-testid={`goal-delete-${g.goal_id}`} title="Delete" className="w-8 h-8 rounded-lg text-[#71717A] hover:text-red-400 hover:bg-white/5 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-sm" data-testid="goal-dialog">
          <DialogHeader><DialogTitle className="font-heading text-lg">{dialog?.goal_id ? "Edit goal" : "New goal"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="goal-title-input" placeholder="Goal title"
              className="w-full bg-[#0d0d0f] border border-white/10 rounded-lg px-3 h-10 text-sm text-white focus:outline-none focus:border-[#D4AF37]/40" />
            <div className="grid grid-cols-2 gap-2">
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger className="bg-[#0d0d0f] border-white/10 text-white" data-testid="goal-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1C1C1F] border-white/10 text-white">{CATS.map((c) => <SelectItem key={c} value={c} className="focus:bg-white/10">{c}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                <SelectTrigger className="bg-[#0d0d0f] border-white/10 text-white" data-testid="goal-priority"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1C1C1F] border-white/10 text-white">{["High", "Medium", "Low"].map((p) => <SelectItem key={p} value={p} className="focus:bg-white/10">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#A1A1AA] mb-1 block">Due date</label>
              <input type="date" value={form.due_date ? form.due_date.slice(0, 10) : ""} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} data-testid="goal-due-date"
                className="w-full bg-[#0d0d0f] border border-white/10 rounded-lg px-3 h-10 text-sm text-white focus:outline-none focus:border-[#D4AF37]/40" />
            </div>
            {dialog?.goal_id && (
              <div>
                <label className="text-xs text-[#A1A1AA] mb-1 block">Progress: {form.progress}%</label>
                <input type="range" min="0" max="100" value={form.progress} onChange={(e) => setForm((f) => ({ ...f, progress: e.target.value }))} data-testid="goal-progress" className="w-full accent-[#D4AF37]" />
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={save} data-testid="goal-save-btn" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full">{dialog?.goal_id ? "Save" : "Add goal"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
